// We only slightly define this schema to make it easier to understand which data to pass in.
import { Type } from '@sinclair/typebox';
import { completePurchaseErrorCodeSchema } from "../completePurchase.mjs";
import { adyenPaymentProvider, paymentProvidersWithOurCompletePurchaseSchema } from "./payment.mjs";
import { cartSchema } from "./cart.mjs";
// We don't want to define it further since the structure of the data is defined by the payment provider.
const adyenProviderDataSchema = Type.Object({
    paymentMethod: Type.Unknown(),
});
const adyenProviderDataPaymentMethodSchema = Type.Object({
    type: Type.Optional(Type.String()),
    brand: Type.Optional(Type.String()),
});
const providerDataSchema = Type.Union([adyenProviderDataSchema]);
export const completePurchaseRequestSchema = Type.Object({
    recaptchaToken: Type.Union([Type.String(), Type.Null()]),
    trackingCookies: Type.Optional(Type.Record(Type.String(), Type.String())),
    paymentProvider: paymentProvidersWithOurCompletePurchaseSchema,
    /**
     * This is an opaque value that is passed through to the payment provider and the type of this
     * value is defined by the payment provider.
     * In the case of Adyen it's the data that is returned from the Adyen SDK when a payment method
     * is selected and the payment is completed. It's passed in the adyen-web package in the onSubmit callback.
     */
    providerData: providerDataSchema,
});
export const completeFreePurchaseRequestSchema = Type.Object({
    recaptchaToken: Type.Union([Type.String(), Type.Null()]),
    trackingCookies: Type.Optional(Type.Record(Type.String(), Type.String())),
});
export const completeInvoicePurchaseRequestSchema = Type.Object({
    recaptchaToken: Type.Union([Type.String(), Type.Null()]),
    trackingCookies: Type.Optional(Type.Record(Type.String(), Type.String())),
});
export const completePurchaseSuccessResponseSchema = Type.Object({
    $type: Type.Literal('success'),
    orderId: Type.String(),
});
export const completePurchaseRedirectResponseSchema = Type.Object({
    $type: Type.Literal('redirect'),
    url: Type.String(),
});
export const completePurchasePostFormResponseSchema = Type.Object({
    $type: Type.Literal('postForm'),
    url: Type.String(),
    data: Type.Record(Type.String(), Type.String()),
    formEncoding: Type.Optional(Type.String()),
});
export const requiresClientProcessingAdyenSchema = Type.Object({
    provider: adyenPaymentProvider,
    // We don't want a more specific schema since the structure of the data is defined by the payment provider.
    // And if we define it the fastify output formatter will skip any properties that are not defined in the schema
    // which will cause the client to not receive the data it needs.
    action: Type.Unknown(),
    environment: Type.Union([Type.Literal('live'), Type.Literal('test')]),
    clientKey: Type.String(),
});
export const completePurchaseRequiresClientProcessingResponseSchema = Type.Object({
    $type: Type.Literal('requiresClientProcessing'),
    orderId: Type.String(),
    clientData: Type.Union([requiresClientProcessingAdyenSchema]),
});
export const completePurchaseFailedResponseSchema = Type.Object({
    $type: Type.Literal('failed'),
    errors: Type.Array(Type.Object({
        code: completePurchaseErrorCodeSchema,
        message: Type.String(),
        type: Type.Union([
            /**
             * An error of type paymentError should be handled and displayed by the payment provider widget and
             * the SDK will make sure to forward such errors to the payment provider widget.
             */
            Type.Literal('paymentError'),
            /**
             * An error of type otherError should be handled and displayed by the checkout page since it's an error
             * that is not related to the payment provider.
             */
            Type.Literal('otherError'),
        ]),
    })),
    /**
     * If this is set the cart has been updated and the client should update the cart in the UI.
     */
    newCart: Type.Optional(cartSchema),
});
/**
 * This happens if we detect a validation error late in the checkout process. It can either be that
 * the PSP has declined the payment or that the customer has entered invalid data.
 */
export const completePurchaseValidationErrorResponseSchema = Type.Object({
    $type: Type.Literal('validationError'),
    errors: Type.Array(Type.Object({ message: Type.String() })),
});
/**
 * This only happens if an internal error in the checkout process occur. This should not happen
 * but we need to have it as a possible outcome so the client can inform the customer that
 * something went wrong.
 */
export const completePurchaseUnexpectedErrorResponseSchema = Type.Object({
    $type: Type.Literal('unexpectedError'),
});
export const completePurchaseResponseSchema = Type.Union([
    completePurchaseSuccessResponseSchema,
    completePurchaseRedirectResponseSchema,
    completePurchasePostFormResponseSchema,
    completePurchaseRequiresClientProcessingResponseSchema,
    completePurchaseFailedResponseSchema,
    completePurchaseUnexpectedErrorResponseSchema,
]);
export const completeFreePurchaseResponseSchema = Type.Union([completePurchaseSuccessResponseSchema]);
export const completeInvoicePurchaseResponseSchema = Type.Union([completePurchaseSuccessResponseSchema]);
//# sourceMappingURL=completePurchase.js.map